<template>
  <div class="notification-container">
    <div
      class="notification-item text-wrap"
      v-for="notification in notifications"
      :key="notification.id"
    >
      <div class="d-flex flex-column">
        <div class="date">
          <i class="fas fa-calendar-alt mr-1"></i>
          {{ notification.formattedDate }}
        </div>
        <div v-html-sanitize="notification.content"></div>
      </div>
      <img
        class="cursor-pointer"
        @click="removeNotification(notification.id)"
        :src="removeIcon.src"
        alt="Remove this Notification"
      />
    </div>
    <div class="d-flex justify-content-center align-items-center">
      <b-button
        v-if="!isLastPage && notifications.length"
        @click="loadMoreNotifications"
        v-show="!loading"
        variant="light"
        class="load-more border mt-1 mb-1"
      >
        Load More...
      </b-button>
      <span v-if="notifications.length === 0">
        No notifications to show
      </span>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import notificationServer from "../../server/notification";
import moment from "moment";
import eocServer from "../../server/eoc";
import formSubmitterServer from "../../server/formSubmitter";
import { mapState } from "vuex";

export default defineComponent({
  name: "CustomNotifications",
  data() {
    return {
      notifications: [],
      loading: false,
      removeIcon: {
        src: require("../../../public/icons/notification-item-remove.png")
      },
      count: 0,
      pageSize: 10,
      loadMoreNotificationTimeout: null,
      notificationsToRemove: [],
      isLastPage: false
    };
  },
  async mounted() {
    await this.loadMoreNotifications();
    this.$root.$on("fetchNotifications", () => this.loadMoreNotifications());
    setTimeout(() => {
      document.body.addEventListener("click", this.handleClick);
    });
  },
  beforeDestroy() {
    document.body.removeEventListener("click", this.handleClick);
  },
  methods: {
    async loadMoreNotifications() {
      this.loading = true;

      const response = await notificationServer.query({
        skip: this.notifications.length,
        pageSize: this.pageSize
      });

      const formattedNotifications = response.notifications.map(n => ({
        id: n.id,
        read: n.read,
        formattedDate: moment(n.dateTime).format("MMMM DD [at] h:mm A"),
        dateTime: n.dateTime,
        content: n.content
      }));

      if (response.notifications.length < this.pageSize) this.isLastPage = true;

      this.count = response.count;

      this.notifications = [
        ...this.notifications,
        ...formattedNotifications
      ].sort((a, b) => {
        if (a.read != b.read) {
          return a.read ? 1 : -1;
        }

        return b.dateTime.localeCompare(a.dateTime);
      });

      this.loading = false;
    },
    async removeNotification(id) {
      const notificationIndex = this.notifications.findIndex(
        item => item.id === id
      );
      this.notifications.splice(notificationIndex, 1);

      this.notificationsToRemove.push(id);

      clearTimeout(this.loadMoreNotificationTimeout);

      this.loadMoreNotificationTimeout = setTimeout(async () => {
        await notificationServer.remove(this.notificationsToRemove);
        await this.loadMoreNotifications();
        this.notificationsToRemove = [];
      }, 3000);
    },
    handleClick(event) {
      this.clickAnchor(event);
    },
    async redirectByEocLocation(eocId, href) {
      const eoc = await eocServer.get(eocId);
      if (eoc.location.id == this.selectedLocation) {
        this.$router.push(href).catch(() => {});
      } else {
        this.$dialog
          .alert(
            {
              title:
                "The element you are trying to access is in a different Location. Are you sure you want to change Locations?"
            },
            { view: "confirm" }
          )
          .then(() => {
            this.$router.push({
              path: href,
              query: { location: eoc.location.id }
            });
          });
      }
    },
    async clickAnchor(event) {
      if (this.$el.contains(event.target) && event.target.tagName === "A") {
        event.preventDefault();
        const href = event.target.getAttribute("href").replace(/\/#\//, "");
        const id = this.getEntityId(href);

        switch (href) {
          case href.match(/episode-of-care/)?.input: {
            this.redirectByEocLocation(id, href);
            break;
          }
          case href.match(/document/)?.input: {
            const doc = await formSubmitterServer.get(id);
            this.redirectByEocLocation(doc.episodeOfCareId, href);
            break;
          }
          case href.match(/home/)?.input: {
            this.$router.push(href).catch(() => {});
            break;
          }
          default: {
            throw new Error("href did not match current implementation");
          }
        }

        this.$emit("input", false);
      }
    },
    getEntityId(href) {
      return href
        .split("/")
        .filter(Boolean)
        .at(-1);
    }
  },
  computed: {
    ...mapState("layout", ["selectedLocation"])
  }
});
</script>

<style scoped lang="scss">
.notification-item {
  width: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  border-radius: 8px;
  background: var(--white, #fff);
  box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.1);
  padding: 15px 21px 15px 21px;
  font-family: Bahnschrift;
  font-style: normal;
  font-size: 14px;
  font-weight: 350;
  margin-bottom: 12px;
  justify-content: space-between;
}

.notification-item:hover {
  border-radius: 8px;
  background: #363abb;
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.17);
  color: #fff;
}

.notification-container {
  overflow-y: auto;
  width: calc(100% - 5px);
  padding-right: 10px;
  height: calc(100vh - 170px);
}

.notification-container::-webkit-scrollbar {
  width: 2px; /* Set the width of the scrollbar */
}

.notification-container::-webkit-scrollbar-thumb {
  background-color: #363abb; /* Set the color of the scrollbar thumb */
  border-radius: 6px; /* Round the edges of the thumb */
  border: 2px solid #363abb;
}

.notification-container::-webkit-scrollbar-thumb:hover {
  background-color: #363abb; /* Change thumb color on hover */
}

.date {
  font-size: 13px;
}

@media (max-width: 1180px) {
  .notification-container {
    min-width: 230px !important;
  }
}
</style>
